import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import {
  OrderSearchParams,
  Order,
  OrderFile,
} from "../../shared/models/order.model";
import { Subject, Subscription } from "rxjs";
import { ServiceMetaData } from "../../shared/models/service-meta.model";
import { BroshService } from "../../shared/services/brosh.service";
import {
  PaginationModel,
  PaginationParams,
  ProcessRunStatusEnum,
} from "../../shared/models/common";
import { RouteEnum } from "src/app/shared/models/routes.model";
import { SharedService } from "src/app/shared/services/shared.service";
import { Router, ActivatedRoute } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { ofType, Actions } from "@ngrx/effects";
import { debounceTime, map, take, takeUntil } from "rxjs/operators";
import {
  FOCUS_ORDER_CHAT_TAB,
  GET_ORDER_HEADER_INIT,
  NAVIGATE_TO_NEXT_ORDER_PAGE,
  NAVIGATE_TO_PREV_ORDER_PAGE,
  PATCH_INBOX_ORDER,
  RELOAD_INBOX,
  RUN_ORDERS_SEARCH,
  SELECT_INBOX_ORDER,
  UI__ROUTE_NAVIGATION,
} from "src/app/shared/ngrx/ngrx-actions";
import {
  inboxNeedsReload,
  inboxResultsPerPage,
  orderSearchMetadata,
  orders,
} from "src/app/shared/ngrx/ngrx-selectors";
import { RealtimeService } from "src/app/shared/services/realtime.service";
import { AuthService } from "src/app/shared/services/auth.service";
import { InboxListComponent } from "./inbox-list/inbox-list.component";
import { InboxFiltersComponent } from "./inbox-filters/inbox-filters.component";
import { ModalPopupComponent } from "src/app/shared/components/ui/modal-popup.component";
import { CommonModule } from "@angular/common";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";

@Component({
  selector: "inbox",
  templateUrl: "./inbox-view.component.html",
  styleUrls: ["./inbox-view.component.scss"],
  imports: [
    InboxListComponent,
    InboxFiltersComponent,
    ModalPopupComponent,
    CommonModule,
    TranslateModule,
    LoaderComponent,
  ],
  standalone: true,
})
export class InboxViewComponent implements OnInit, AfterViewInit {
  @ViewChild('inboxFilters') inboxFilters: InboxFiltersComponent;

  ordersLoading: boolean;
  searchSubscription: Subscription;
  routeEnum: RouteEnum = new RouteEnum();
  sub: Subscription;
  displayRXDownloadStatusDialog: boolean;
  rxDownloadLoading: boolean;
  rxDownloadFileUrl: string;
  processStatusTimer: any;
  getProcessStatusIntervalSeconds: number = 2;
  rxDownloadToken: number = 0;
  destroy$ = new Subject();
  private pageReload$ = new Subject<void>();
  // bumped by every search the user starts; a quiet re-run that crosses one drops its answer
  private searchSeq = 0;
  displayOrders: Order[];
  numberOfRows: number;
  pendingOrderSelection: 'first' | 'last' | null = null;
  shouldFocusOrderId: boolean = false;

  rangeFrom: number = 1;
  rangeTo: number = 50;
  totalRecords: number;

  searchParams: OrderSearchParams = {
    rangeTo: this.rangeTo,
    rangeFrom: this.rangeFrom,
    searchId: null,
  };

  get orderSearchMetadata(): ServiceMetaData {
    return this.store.selectSignal(orderSearchMetadata)() ?? {};
  }

  constructor(
    private readonly store: Store,
    private broshService: BroshService,
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private actions$: Actions,
    private realtime: RealtimeService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.store
      .select(orders)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sm) => {
        this.displayOrders = sm;
        // After orders are loaded, select the pending order if any
        if (this.pendingOrderSelection && sm && sm.length > 0) {
          const orderToSelect = this.pendingOrderSelection === 'first'
            ? sm[0]
            : sm[sm.length - 1];
          this.store.dispatch(
            SELECT_INBOX_ORDER({
              order: orderToSelect,
            })
          );
          this.pendingOrderSelection = null;
        }
      });

    this.store
      .select(inboxResultsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sm) => {
        this.numberOfRows = sm;
      });

    // check query params
    this.sub = this.route.queryParams.subscribe((params) => {
      if (params && params["chatCode"]) {
        this.resetSearchParams();
        this.searchParams.chatCode = params["chatCode"];
        this.runOrdersSearch(true);
      } else if (params && params["summaryCode"]) {
        this.resetSearchParams();
        this.searchParams.summaryCode = params["summaryCode"];
        this.runOrdersSearch(true);
      } else if (!this.orderSearchMetadata.searchId) {
        this.resetSearchParams();
        this.runOrdersSearch(true);
      } else if (this.store.selectSignal(inboxNeedsReload)()) {
        this.runOrdersSearch(true);
      }

      // Check if we should focus orderIdMmd input
      if (params && params["focusOrderId"] === 'true') {
        this.shouldFocusOrderId = true;
        this.focusOrderIdIfNeeded();
      }
    });

    this.listenForNudges();

    this.actions$
      .pipe(
        ofType(RELOAD_INBOX),
        takeUntil(this.destroy$),
        map(({ }) => {
          this.runOrdersSearch(true);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(NAVIGATE_TO_NEXT_ORDER_PAGE),
        takeUntil(this.destroy$),
        map(({ }) => {
          this.navigateToNextPage();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(NAVIGATE_TO_PREV_ORDER_PAGE),
        takeUntil(this.destroy$),
        map(({ }) => {
          this.navigateToPrevPage();
        })
      )
      .subscribe();
  }

  // The push channel only says that something changed; the search is re-run
  // (or one row patched) so a repeated nudge leaves the same state. Intake
  // nudges go to lab users only, so clinic and plant inboxes do not listen.
  private listenForNudges() {
    if (!this.authService.currentUser.isLabUser) return;
    this.realtime.nudges$.pipe(takeUntil(this.destroy$)).subscribe((n) => {
      if (n.type !== "INTAKE_CASE") return;
      if (n.state === "NEW") {
        this.pageReload$.next();
        return;
      }
      const row = this.displayOrders?.find((o) => o.orderId === n.orderId);
      if (!row) return;
      if ((n.state === "TAKEN" || n.state === "FREE") && row.intake) {
        const taken = n.state === "TAKEN";
        this.store.dispatch(
          PATCH_INBOX_ORDER({
            orderId: n.orderId,
            changes: {
              // the nudge carries no taker id, so a taken row has none until reload
              intake: {
                ...row.intake,
                takerUserId: null,
                takerName: taken ? n.takerName ?? null : null,
              },
            },
          })
        );
      } else if (n.state === "ACCEPTED" || n.state === "UPDATED" || n.state === "CANCELLED") {
        // quiet: a background refresh pops no error; the row waits for the next reload
        this.broshService
          .getOrderHeaderSingle(n.orderId, false, true)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (fresh) =>
              this.store.dispatch(
                PATCH_INBOX_ORDER({ orderId: n.orderId, changes: { ...fresh } })
              ),
            error: () => {},
          });
      }
    });

    this.realtime.connected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pageReload$.next());

    // a burst of triggers becomes one page load, so responses cannot cross
    this.pageReload$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(() => this.rerunPage());
  }

  // A search is a stored snapshot: get_order_header_init_json keeps the matching
  // order ids under a search id, the page call only re-reads those rows, and
  // snapshots older than 2 h are deleted. So a re-read would never show a new
  // case, and goes blank once the snapshot is gone. The search is re-run
  // instead: same filters, same page, quietly (no scroll, no auto-open of a
  // single result, no error dialog).
  private rerunPage() {
    if (!this.searchParams.searchId) return;
    const params = this.searchParams;
    const seq = this.searchSeq;
    this.broshService.getOrderHeaderInit(params, true, params.searchId).subscribe({
      next: (data) => {
        // a search the user started meanwhile wins; a reply without a search id changes nothing
        if (seq !== this.searchSeq || !data?.searchId) return;
        this.store.dispatch(
          GET_ORDER_HEADER_INIT({
            // keep the page's range so the paginator does not jump while the page loads
            orderSearchMetadata: {
              ...this.orderSearchMetadata,
              totalRecords: data.totalRecords,
              searchId: data.searchId,
            },
          })
        );
        params.searchId = data.searchId;
        this.clampRange(params, data.totalRecords ?? 0);
        this.getPageResults(false, true);
      },
      // the next nudge or reconnect tries again
      error: () => {},
    });
  }

  // The current page is past the end when the total shrank: move to the last page.
  private clampRange(params: OrderSearchParams, total: number) {
    if (params.rangeFrom <= Math.max(total, 1)) return;
    const size = this.numberOfRows || params.rangeTo - params.rangeFrom + 1;
    params.rangeFrom = total > 0 ? Math.floor((total - 1) / size) * size + 1 : 1;
    params.rangeTo = params.rangeFrom + size - 1;
  }

  resetSearchParams() {
    this.searchParams = new OrderSearchParams();
    this.searchParams.rangeFrom = this.rangeFrom;
    this.searchParams.rangeTo = this.rangeTo;
  }

  ngAfterViewInit(): void {
    this.focusOrderIdIfNeeded();
  }

  focusOrderIdIfNeeded(): void {
    if (this.shouldFocusOrderId && this.inboxFilters) {
      setTimeout(() => {
        this.inboxFilters.focusOrderIdInput();
        this.shouldFocusOrderId = false;
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
    if (this.sub) this.sub.unsubscribe();
    if (this.processStatusTimer) clearInterval(this.processStatusTimer);
  }

  onResultsPerPageChanged() {
    this.searchParams.rangeFrom = 1;
    this.searchParams.rangeTo = this.numberOfRows;
    this.runOrdersSearch(true);
  }

  getOrdersBySearch(params: OrderSearchParams) {
    console.log(params);
    this.searchParams = new OrderSearchParams();
    this.searchParams.rangeFrom = 1;
    this.searchParams.rangeTo = this.numberOfRows;

    this.searchParams = Object.assign(
      this.searchParams,
      this.orderSearchMetadata,
      params
    );
    this.runOrdersSearch(true);
  }

  loadDataOnPagination(event: any) {
    if (
      !this.displayOrders ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.orderSearchMetadata.searchId;
    this.runOrdersSearch(false);
  }

  runOrdersSearch(newSearch: boolean) {
    this.searchSeq++;
    this.ordersLoading = true;

    var element = document.getElementById("ordersSearch");
    if (element) element.scrollIntoView(true);

    this.store.dispatch(
      RUN_ORDERS_SEARCH({
        newSearch: newSearch ? true : false,
      })
    );

    if (newSearch) {
      const prevSearchId = this.searchParams.searchId;
      this.searchParams.searchId = null;
      this.broshService.getOrderHeaderInit(this.searchParams, false, prevSearchId).subscribe(
        (data) => {
          this.store.dispatch(
            GET_ORDER_HEADER_INIT({
              orderSearchMetadata: data,
            })
          );

          this.searchParams.searchId = data.searchId;
          this.getPageResults(true);
        },
        () => (this.ordersLoading = false)
      );
    } else {
      this.getPageResults(false);
    }
  }

  getPageResults(newSearch: boolean = false, quiet: boolean = false) {
    console.log(this.searchParams);
    this.broshService
      .getOrderHeaderPage(
        this.searchParams.searchId,
        this.searchParams.rangeFrom,
        this.searchParams.rangeTo,
        this.searchParams.qcModeSw,
        quiet
      )
      .subscribe(
        (pageOrders) => {
          this.ordersLoading = false;
          if (newSearch && pageOrders && pageOrders.length === 1) {
            this.onOrderSelected(pageOrders[0]);
          }
        },
        () => (this.ordersLoading = false)
      );
  }

  navigateToNextPage() {
    const metadata = this.orderSearchMetadata;
    if (!metadata?.toRecord || !metadata?.totalRecords) return;

    // Calculate next page range
    const currentTo = metadata.toRecord;
    const nextFrom = currentTo + 1;
    const nextTo = Math.min(currentTo + this.numberOfRows, metadata.totalRecords);

    if (nextFrom > metadata.totalRecords) return;

    // Set flag to select first order after pagination
    this.pendingOrderSelection = 'first';

    // Trigger pagination
    this.loadDataOnPagination({
      rangeFrom: nextFrom,
      rangeTo: nextTo,
    });
  }

  navigateToPrevPage() {
    const metadata = this.orderSearchMetadata;
    if (!metadata?.fromRecord || metadata.fromRecord <= 1) return;

    // Calculate previous page range
    const currentFrom = metadata.fromRecord;
    const prevTo = currentFrom - 1;
    const prevFrom = Math.max(1, prevTo - this.numberOfRows + 1);

    // Set flag to select last order after pagination
    this.pendingOrderSelection = 'last';

    // Trigger pagination
    this.loadDataOnPagination({
      rangeFrom: prevFrom,
      rangeTo: prevTo,
    });
  }

  onOrderSelected(order: Order) {
    this.store.dispatch(
      SELECT_INBOX_ORDER({
        order: order,
      })
    );

    this.store.dispatch(
      UI__ROUTE_NAVIGATION({
        data: { link: this.routeEnum.URL_ORDER },
      })
    );

    this.router.navigate([this.routeEnum.URL_ORDER]);
  }

  onGoToChat(order: Order) {
    this.store.dispatch(
      FOCUS_ORDER_CHAT_TAB({
        focusOrderChatTab: true,
      })
    );

    this.onOrderSelected(order);
  }

  onMarkFavorite(order: Order) {
    let favoriteSw = !order.favoriteSw;
    this.broshService.updateOrderFavorite(order, favoriteSw).subscribe(
      () => {
        order.favoriteSw = favoriteSw;
      },
      (error) => this.sharedService.alertError(error)
    );
  }

  onDownloadSelectedRX() {
    let files = [];
    let orders = this.displayOrders.filter((o) => o.checkedSw);
    orders.forEach((o) => {
      files.push(...o.filesRX);
    });
    if (files.length > 0) this.downloadRX(files);
  }

  onDownloadRXOnPage() {
    let files = [];
    let orders = this.displayOrders;
    orders.forEach((o) => {
      files.push(...o.rxToDownload);
    });
    if (files.length > 0) this.downloadRX(files);
  }

  downloadRX(files: OrderFile[]) {
    this.displayRXDownloadStatusDialog = true;

    // invalidate any previous download still in flight — its timer and its
    // late HTTP responses must not touch the new download's state
    const token = ++this.rxDownloadToken;

    this.broshService.createRXDownloadReq(files).subscribe((runNum) => {
      if (token != this.rxDownloadToken) return;
      this.rxDownloadLoading = true;

      if (this.processStatusTimer) {
        clearInterval(this.processStatusTimer);
      }

      const timer = setInterval(() => {
        if (token != this.rxDownloadToken) {
          clearInterval(timer);
          return;
        }
        this.broshService.getProcessStatus(runNum).subscribe(
          (data) => {
            if (token != this.rxDownloadToken) return;
            if (data.status == ProcessRunStatusEnum.FINISHED) {
              clearInterval(timer);
              this.rxDownloadLoading = false;
              this.rxDownloadFileUrl = data.outFileUrl;
              console.log(data.outFileUrl);
            } else if (data.status == ProcessRunStatusEnum.FAILED) {
              this.downloadFileError(
                this.translateService.instant(
                  "translate.alert.downloadRxProcessFailed"
                )
              );
            }
          },
          (err) => {
            if (token != this.rxDownloadToken) return;
            this.downloadFileError(err);
          }
        );
      }, this.getProcessStatusIntervalSeconds * 1000);
      this.processStatusTimer = timer;
    });
  }

  downloadFileError(err) {
    clearInterval(this.processStatusTimer);
    this.rxDownloadLoading = false;
    this.sharedService.alertError(err);
  }
}
